import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/solid";
import classNames from "classnames";
import PropTypes, { oneOf } from "prop-types";
import React, { useRef } from "react";
import CurrencyInput from "react-currency-input-field";
import { mergeRefs } from "react-merge-refs";
import classes from "./Input.module.scss";

export const Input = React.forwardRef(
  (
    {
      type,
      name,
      placeholder,
      label,
      defaultValue,
      value,
      ignoreOffsetTop,
      required,
      onChange,
      onBlur,
      startAdornment,
      endAdornment,
      shadow = false,
      white = false,
      containerClassName,
      divider = false,
      endDivider = false,
      startDivider = false,
      pattern,
      error = null,
      decimalsLimit = 2,
      ...props
    },
    ref
  ) => {
    const localRef = useRef();
    const inputRef = mergeRefs([localRef, ref]);
    const iconRef = useRef();
    const toggleInputType = () => {
      const icon = iconRef.current;
      switch (localRef.current?.type) {
        case "password":
          localRef.current.type = "text";
          icon?.classList.remove("icon-eye");
          icon?.classList.add("icon-eye-off");
          return;
        case "text":
          localRef.current.type = "password";
          icon?.classList.remove("icon-eye-off");
          icon?.classList.add("icon-eye");
          return;
        default:
          return;
      }
    };
    let localType = [
      "text",
      "email",
      "tel",
      "number",
      "password",
      "currency",
    ].includes(type)
      ? type
      : "text";

    const isCurrency = localType === "currency";

    const handleCurrencyControllerOnClick = (e, rate) => {
      e.preventDefault();
      e.stopPropagation();
      if (onChange) {
        onChange({
          target: {
            name,
            value: parseFloat(
              parseFloat(value || 0) +
                Number(
                  parseFloat(rate * (parseFloat(props.step) || 1)).toFixed(2)
                )
            ).toFixed(2),
          },
        });
      }
    };
    return (
      <>
        <label
          data-testid={"label"}
          className={classNames(classes["label"], {
            "mt-0": ignoreOffsetTop,
            [classes["shadow"]]: shadow,
            [classes["error"]]: error,
          })}
        >
          {label && (
            <div
              data-testid={"label-text"}
              className={classNames(classes["label-text"], {
                [classes["required"]]: required,
              })}
            >
              {label}
            </div>
          )}
          <div
            className={classNames(classes.wrapper, containerClassName, {
              [classes["white"]]: white,
              [classes["disabled"]]: props.disabled,
            })}
          >
            {startAdornment && (
              <span
                className={
                  classes["start-adornment"] +
                  " " +
                  (divider || startDivider ? classes["divider"] : "")
                }
              >
                {startAdornment}
              </span>
            )}
            <div className={classes["input-wrapper"]}>
              {isCurrency ? (
                <div className="d-flex">
                  <CurrencyInput
                    ref={inputRef}
                    name={name}
                    step={parseFloat(props.step) || 1}
                    placeholder={placeholder}
                    defaultValue={defaultValue}
                    value={value || ""}
                    decimalsLimit={decimalsLimit}
                    onValueChange={(value, name) => {
                      if (onChange)
                        onChange({ target: { name, value: value || "" } });
                    }}
                    onBlur={onBlur}
                    className={classNames(classes.input, props.className, {
                      [classes.currency]: isCurrency,
                    })}
                  />
                  <div className={classes.currencyControllersWrapper}>
                    <button
                      tabIndex={-1}
                      className={classes.currencyControllers}
                      onClick={(e) => handleCurrencyControllerOnClick(e, 1)}
                    >
                      <ChevronUpIcon />
                    </button>
                    <button
                      tabIndex={-1}
                      className={classes.currencyControllers}
                      onClick={(e) => handleCurrencyControllerOnClick(e, -1)}
                    >
                      <ChevronDownIcon />
                    </button>
                  </div>
                </div>
              ) : (
                <input
                  data-testid="textbox"
                  ref={inputRef}
                  name={name}
                  type={localType}
                  placeholder={placeholder}
                  defaultValue={defaultValue}
                  required={required || false}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  {...props}
                  className={classNames(classes.input, props.className, {
                    [classes.currency]: isCurrency,
                  })}
                  pattern={pattern}
                />
              )}
              {localType === "password" && (
                <div
                  data-testid={"eye-icon"}
                  ref={iconRef}
                  className={`${classes["toggle-password"]} icon-eye`}
                  onClick={toggleInputType}
                />
              )}
            </div>
            {endAdornment && (
              <span
                className={
                  classes["end-adornment"] +
                  " " +
                  (divider || endDivider ? classes["divider"] : "")
                }
              >
                {endAdornment}
              </span>
            )}
          </div>
          {error && (
            <span className={classNames(classes["error-text"])}>{error}</span>
          )}
        </label>
      </>
    );
  }
);

Input.propTypes = {
  /**
   * input type
   */
  type: oneOf(["text", "tel", "email", "number", "password", "currency"]),
  /**
   * name of input tag
   */
  name: PropTypes.string,
  /**
   * Label to be shown above the input
   */
  label: PropTypes.string,
  /**
   * Placeholder inside the input
   */
  placeholder: PropTypes.string,
  /**
   * set true to ignore margin top of an input
   */
  ignoreOffsetTop: PropTypes.bool,
  /**
   * marks input as required
   */
  required: PropTypes.bool,
  /**
   * onChange function
   */
  onChange: PropTypes.func,
  /**
   * onBlur function
   */
  onBlur: PropTypes.func,
};

Input.defaultProps = {
  type: "text",
  label: undefined,
  placeholder: "Input Here",
};
